import { apiClient } from "api/ApiClient";
import ApiEndpoints from "api/ApiEndpoints";
import store from "redux/store";
import { setCards, setSetup, setLastPayment } from "./BillingSlice";

// Fetches a SetupIntent client secret used to tokenise a new card.
export const getSetup = () => {
  return apiClient
    .get(ApiEndpoints.BILLING_SETUP)
    .then((res) => {
      const clientSecret = res?.data?.client_secret;
      if (!clientSecret) return false;
      store.dispatch(setSetup(clientSecret));
      return true;
    })
    .catch(() => false);
};

export const getCards = () => {
  return apiClient
    .get(ApiEndpoints.BILLING_CARDS)
    .then((res) => {
      store.dispatch(setCards(res?.data || []));
      return true;
    })
    .catch(() => false);
};

export const addCard = (paymentMethodId) => {
  return apiClient
    .post(ApiEndpoints.BILLING_CARDS, { paymentMethodId })
    .then(() => getCards())
    .catch(() => false);
};

export const updateCardBilling = (id, billingDetails) => {
  return apiClient
    .put(ApiEndpoints.BILLING_CARD(id), { billing_details: billingDetails })
    .then(() => getCards())
    .catch(() => false);
};

export const setActiveCard = (id) => {
  return apiClient
    .post(ApiEndpoints.BILLING_CARD_DEFAULT(id))
    .then(() => getCards())
    .catch(() => false);
};

export const deleteCard = (id) => {
  return apiClient
    .delete(ApiEndpoints.BILLING_CARD(id))
    .then(() => getCards())
    .catch(() => false);
};

export const getLastPayment = () => {
  return apiClient
    .get(ApiEndpoints.BILLING_LAST_PAYMENT)
    .then((res) => {
      store.dispatch(setLastPayment(res?.data || null));
      return true;
    })
    .catch(() => false);
};

export const loadBilling = () =>
  Promise.all([getSetup(), getCards(), getLastPayment()]);

export const getSubscriptionConfig = () => {
  return apiClient
    .get(ApiEndpoints.SUBSCRIPTION_CONFIG)
    .then((res) => res?.data || null)
    .catch(() => null);
};

export const saveSubscriptionConfig = (payload) => {
  return apiClient
    .put(ApiEndpoints.SUBSCRIPTION_CONFIG, payload)
    .then(() => true)
    .catch(() => false);
};

// Clears a scheduled end-of-period cancellation so the subscription renews.
export const resumeSubscription = () => {
  return apiClient
    .post(ApiEndpoints.SUBSCRIPTION_RESUME)
    .then(() => true)
    .catch(() => false);
};

// Pauses payment collection (keep_as_draft) — no charges while paused.
export const pauseSubscription = () => {
  return apiClient
    .post(ApiEndpoints.SUBSCRIPTION_PAUSE)
    .then(() => true)
    .catch(() => false);
};

// Resumes a paused subscription. voidDrafts=true forgives the paused periods
// (deletes their draft invoices); false keeps the drafts for manual collection.
export const unpauseSubscription = (voidDrafts) => {
  return apiClient
    .post(ApiEndpoints.SUBSCRIPTION_UNPAUSE, { voidDrafts })
    .then(() => true)
    .catch(() => false);
};

// Adds a one-off amount to the partner's next subscription invoice.
export const addOneTimeCharge = (payload) => {
  return apiClient
    .post(ApiEndpoints.SUBSCRIPTION_CHARGE, payload)
    .then(() => true)
    .catch(() => false);
};

// Pending (not-yet-invoiced) one-time charges — still editable/deletable.
export const getOneTimeCharges = () => {
  return apiClient
    .get(ApiEndpoints.SUBSCRIPTION_CHARGE)
    .then((res) => res?.data || [])
    .catch(() => []);
};

export const updateOneTimeCharge = (id, payload) => {
  return apiClient
    .put(ApiEndpoints.SUBSCRIPTION_CHARGE_ID(id), payload)
    .then(() => true)
    .catch(() => false);
};

export const deleteOneTimeCharge = (id) => {
  return apiClient
    .delete(ApiEndpoints.SUBSCRIPTION_CHARGE_ID(id))
    .then(() => true)
    .catch(() => false);
};

// Read-only preview of the partner's next invoice (base + pending one-offs).
export const getUpcomingInvoice = () => {
  return apiClient
    .get(ApiEndpoints.SUBSCRIPTION_UPCOMING)
    .then((res) => res?.data || null)
    .catch(() => null);
};
